/** Entry point of the standalone build: exposes the `KapiaWidget` global, and the same API as `PleigroundWidget`, the name embeds written before the rename call. */

import React from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import type { KapiaWidgetConfig, KapiaWidgetAPI, SetPlacementArg, WebchatConfig } from './types';
import { BrainWebchatApiClient } from './brainApi';
import { readCookieParams } from './cookieParams';
import { Widget, type WidgetControls } from './Widget';
import { SearchbarWidget } from './SearchbarWidget';
import { WidgetRootContext } from './WidgetRootContext';
import { adoptLegacyVisitorStorage } from './legacyStorage';
import { BASE_LANGUAGE, normalizeLanguage, WidgetLangContext } from '../i18n';

// Bundled into `globalThis.__KAPIA_WIDGET_CSS__` (vite.config.standalone.ts)
// for injection into the shadow root.
import '../styles/tokens.css';
import '../styles/animations.css';
import './shadow-reset.css';

// `data-pleiground-searchbar` is the marker pages placed before the rename.
const SEARCHBAR_MARKER_SELECTOR = '[data-kapia-searchbar], [data-pleiground-searchbar]';

/** Inject the widget's bundled CSS into a shadow root, isolating it from the host page. */
function injectStyles(shadow: ShadowRoot): void {
  const css = globalThis.__KAPIA_WIDGET_CSS__;
  if (!css) return;
  const style = document.createElement('style');
  style.textContent = css;
  shadow.appendChild(style);
}

let root: Root | null = null;
let container: HTMLDivElement | null = null;
let apiClient: BrainWebchatApiClient | null = null;

// Imperative handles reported by the mounted `Widget` via `onReady`; they act on
// live React state, so no open state is tracked here.
let controls: WidgetControls | null = null;

// A `setPlacement` call made before `controls` exists (init() is async and
// awaits the config fetch). Only the last call before mount matters.
let pendingPlacement: SetPlacementArg | null = null;

// `setPlacement` is meaningless for the searchbar layout (it mounts into a
// host-page marker, not a floating bubble/window), so it never gets `onReady`.
let isSearchbarLayout = false;

// Monotonic token making init() idempotent: a run superseded by a newer init()
// or destroy() while awaiting config aborts rather than mount a second widget.
// Hosts routinely call init() twice on a cold load (SPA hydration).
let mountToken = 0;

const WIDGET_CONTAINER_ID = 'kapia-widget-container';

/**
 * Remove any widget container already under a parent, such as one from a
 * duplicate bundle load the mount token cannot see.
 */
function removeExistingContainers(parent: ParentNode): void {
  parent
    .querySelectorAll(`#${WIDGET_CONTAINER_ID}`)
    .forEach((el) => el.remove());
}

async function init(config: KapiaWidgetConfig): Promise<void> {
  if (!config.apiKey) {
    throw new Error('KapiaWidget: apiKey is required');
  }

  // The token is claimed before the first await, so a later init() or destroy()
  // supersedes this run.
  destroy();
  const token = mountToken;
  adoptLegacyVisitorStorage();

  const verification =
    config.signature != null && config.issuedAt != null
      ? { signature: config.signature, issuedAt: config.issuedAt }
      : undefined;
  // Only a proposal: the backend decides, since the agent may pin a language.
  const lang =
    config.lang ||
    (typeof document !== 'undefined'
      ? document.documentElement.lang || undefined
      : undefined);
  apiClient = new BrainWebchatApiClient(
    config.apiKey,
    config.apiBaseUrl,
    lang,
    config.params,
    verification,
  );

  let webchatConfig: WebchatConfig;
  try {
    webchatConfig = await apiClient.fetchConfig();
  } catch (error) {
    console.error('KapiaWidget: Failed to fetch config', error);
    throw error;
  }

  // Superseded while awaiting config.
  if (token !== mountToken) return;

  // Chrome follows the language the backend served the texts in, so they agree.
  const chromeLang =
    normalizeLanguage(webchatConfig.resolvedLanguage) ??
    normalizeLanguage(lang) ??
    BASE_LANGUAGE;

  // A snapshot at init: a cookie set later reaches the bag only through
  // KapiaWidget.setParams().
  const cookieParams = readCookieParams(webchatConfig.paramFields);
  if (Object.keys(cookieParams).length > 0) {
    apiClient.foldParams(cookieParams);
  }

  if (webchatConfig.layoutVariant === 'searchbar') {
    isSearchbarLayout = true;
    if (pendingPlacement) {
      console.warn('KapiaWidget: setPlacement has no effect on the searchbar layout.');
      pendingPlacement = null;
    }

    const marker = document.querySelector<HTMLElement>(SEARCHBAR_MARKER_SELECTOR);
    if (!marker) {
      console.warn(
        `KapiaWidget: layout_variant=searchbar but no ${SEARCHBAR_MARKER_SELECTOR} marker found on the page; nothing rendered. Place the [kapia_searchbar] shortcode (or a matching element) where the inline searchbar should appear.`,
      );
      return;
    }

    // The marker stays in the light DOM so host layout positions it; the shadow
    // root lives on a child.
    removeExistingContainers(marker);
    container = document.createElement('div');
    container.id = WIDGET_CONTAINER_ID;
    container.style.cssText = 'display: contents;';
    marker.appendChild(container);

    const shadow = container.attachShadow({ mode: 'open' });
    injectStyles(shadow);

    const mountPoint = document.createElement('div');
    mountPoint.id = 'kapia-widget-root';
    shadow.appendChild(mountPoint);

    root = createRoot(mountPoint);
    root.render(
      <React.StrictMode>
        <WidgetRootContext.Provider value={shadow}>
          <WidgetLangContext.Provider value={chromeLang}>
            <SearchbarWidget webchatConfig={webchatConfig} apiClient={apiClient} />
          </WidgetLangContext.Provider>
        </WidgetRootContext.Provider>
      </React.StrictMode>,
    );

    console.log('KapiaWidget: Initialized searchbar layout');
    return;
  }

  removeExistingContainers(document.body);
  container = document.createElement('div');
  container.id = WIDGET_CONTAINER_ID;
  container.style.cssText = 'position: fixed; z-index: 999999; pointer-events: none;';
  document.body.appendChild(container);

  const shadow = container.attachShadow({ mode: 'open' });
  injectStyles(shadow);

  const mountPoint = document.createElement('div');
  mountPoint.id = 'kapia-widget-root';
  mountPoint.style.cssText = 'pointer-events: auto;';
  shadow.appendChild(mountPoint);

  root = createRoot(mountPoint);
  root.render(
    <React.StrictMode>
      <WidgetRootContext.Provider value={shadow}>
        <WidgetLangContext.Provider value={chromeLang}>
          <Widget
            config={config}
            webchatConfig={webchatConfig}
            apiClient={apiClient}
            onReady={(receivedControls) => {
              controls = receivedControls;
              if (pendingPlacement) {
                controls.setPlacement(pendingPlacement);
                pendingPlacement = null;
              }
            }}
          />
        </WidgetLangContext.Provider>
      </WidgetRootContext.Provider>
    </React.StrictMode>,
  );

  console.log('KapiaWidget: Initialized successfully');
}

function destroy(): void {
  // Invalidate any init() currently awaiting config so it won't mount.
  mountToken++;

  if (root) {
    root.unmount();
    root = null;
  }

  if (container && container.parentNode) {
    container.parentNode.removeChild(container);
    container = null;
  }

  apiClient = null;
  controls = null;
  pendingPlacement = null;
  isSearchbarLayout = false;

  console.log('KapiaWidget: Destroyed');
}

function open(): void {
  controls?.setOpen(true);
}

function close(): void {
  controls?.setOpen(false);
}

function toggle(): void {
  controls?.setOpen((prev) => !prev);
}

/** Reposition the widget at runtime; a call before mount is queued. */
function setPlacement(placement: SetPlacementArg): void {
  if (isSearchbarLayout) {
    console.warn('KapiaWidget: setPlacement has no effect on the searchbar layout.');
    return;
  }
  if (controls) {
    controls.setPlacement(placement);
  } else {
    pendingPlacement = placement;
  }
}

/** Refresh the embed params bag after init (e.g. the visitor logged in). */
function setParams(
  params: Record<string, unknown>,
  verification?: { signature: string; issuedAt: number },
): void {
  apiClient?.setParams(params, verification);
}

const KapiaWidget: KapiaWidgetAPI = {
  init,
  destroy,
  open,
  close,
  toggle,
  setParams,
  setPlacement,
};

if (typeof window !== 'undefined') {
  window.KapiaWidget = KapiaWidget;
  window.PleigroundWidget = KapiaWidget;
}

export { KapiaWidget };
